import "./ClientRegistrationCommunicationReferralDetailsComponent.scss";
import * as Yup from "yup";
import {IClientCommunicationReferralDetails} from "../../../shared/models/client.model";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ImageConfig, Misc} from "../../../constants";
import FormDebuggerComponent from "../../../shared/components/form-debugger/FormDebuggerComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikSelectDropdownComponent
    from "../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import DataLabelValueComponent from "../../../shared/components/data-label-value/DataLabelValueComponent";
import HorizontalLineComponent
    from "../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import {
    // getClientAuthorizationDiscloseProtectedHealthInformation,
    getClientCommunicationReferralDetails
} from "../../../store/actions/client.action";
import FormikTextAreaComponent from "../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import {setFormChangesData} from "../../../store/actions/form-state.action";
import FormikRadioButtonGroupComponent
    from "../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SuccessPopupComponent from "../../../shared/components/success-popup/SuccessPopupComponent";
import {useNavigate} from "react-router-dom";


interface ClientRegistrationCommunicationReferralDetailsComponentProps {
    onNext: () => void;
    appointmentId: any;
}

const ClientCommunicationReferralDetailsFormValidationSchema = Yup.object({
    communication_preferences: Yup.object({
        appointment_reminders: Yup.string().required('Appointment Reminders is required'),
        appointment_confirmations: Yup.string().required('Appointment Confirmations is required')
    }),

    referral_details: Yup.object({

        source_info_first_name: Yup.string()
            .nullable()
            .when('source', {
                is: (val: any) => val === 'friends_family_colleague',
                then: Yup.string().required('First Name is required'),
                otherwise: Yup.string()
            }),
        source_info_last_name: Yup.string()
            .nullable()
            .when('source', {
                is: (val: any) => val === 'friends_family_colleague',
                then: Yup.string().required('Last Name is required'),
                otherwise: Yup.string()
            }),
        source_platform: Yup.mixed()
            .nullable()
            .when('source', {
                is: (val: any) => val === 'social_media',
                then: Yup.string().required('Name is required'),
                otherwise: Yup.string()
            }),


        source_info_phone: Yup.string()
            .nullable()
            .when('source', {
                is: 'friends_family_colleague',
                then: Yup.string()
                    .required('Phone Number is required')
                    .test('is-ten-digits', 'Phone number must contain exactly 10 digits', (value) => {
                        return value?.length === 10;
                    }),
                otherwise: Yup.string()
            }),

        source_info_email: Yup.string()
            .nullable()
            .email('Invalid email')
            .when('source', {
                is: 'friends_family_colleague',
                then: Yup.string().required('Email is required'),
                otherwise: Yup.string()
            })
    })
});

const ClientCommunicationReferralDetailsFormInitialValues: IClientCommunicationReferralDetails = {
    communication_preferences: {
        appointment_reminders: "",
        appointment_confirmations: ""
    },
    referral_details: {
        source: "",
        source_platform: "",
        source_info_first_name: "",
        source_info_last_name: "",
        source_info_phone: "",
        source_info_email: "",
        source_info_relationship: "",
        comment: ''
    }
}

const ClientRegistrationCommunicationReferralDetailsComponent = (props: ClientRegistrationCommunicationReferralDetailsComponentProps) => {

    const {onNext, appointmentId} = props;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [clientCommunicationReferralDetailsFormInitialValues, setClientCommunicationReferralDetailsFormInitialValues] = useState<IClientCommunicationReferralDetails>(_.cloneDeep(ClientCommunicationReferralDetailsFormInitialValues));
    const [isFormSubmissionSuccessful, setIsFormSubmissionSuccessful] = useState<boolean>(false);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const {systemSettingsDetails} = useSelector((state: IRootReducerState) => state.user)
    const {
        clientCommunicationReferralDetails,
        isClientCommunicationReferralDetailsLoaded,
    } = useSelector((state: IRootReducerState) => state.client);

    const {
        socialMediaPlatformList,
        communicationModeTypeList,
        referralTypeList,
        relationshipList
    } = useSelector((state: IRootReducerState) => state.staticData);

    useEffect(() => {
        if (clientCommunicationReferralDetails) {
            setClientCommunicationReferralDetailsFormInitialValues(clientCommunicationReferralDetails);
        }
    }, [clientCommunicationReferralDetails, isClientCommunicationReferralDetailsLoaded]);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = {
            ...values, is_communication_details_updated: true,
            appointment_id: appointmentId ? appointmentId : currentUser?.appointment_id
        };
        payload.is_communication_details_filled = true;
        CommonService._client.ClientCommunicationReferralDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setSubmitting(false);
                dispatch(getClientCommunicationReferralDetails());
                dispatch(setFormChangesData(false));
                if (systemSettingsDetails?.other_settings?.waiver_enabled && !currentUser?.is_profile_completed) {
                    onNext();
                } else {
                    if (currentUser?.onboarding_forms_type === "default" || currentUser?.onboarding_forms_type === "expired_forms") {
                        setIsFormSubmissionSuccessful(true);
                    } else {
                        onNext()
                    }
                }
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [dispatch, currentUser, onNext, appointmentId, systemSettingsDetails?.other_settings?.waiver_enabled]);

    return (
        <div
            className={'client-registration-details-block client-registration-communication-referral-details-component'}>
            <div className="client-registration-details-block__header">
                <div className="client-registration-details-block__title">
                    Communication Preferences
                </div>
            </div>
            <div className="client-registration-details-block__body">
                <Formik
                    validationSchema={ClientCommunicationReferralDetailsFormValidationSchema}
                    initialValues={clientCommunicationReferralDetailsFormInitialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={onSubmit}
                >
                    {({values, setFieldValue, validateForm, isValid, isSubmitting, dirty}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            if (dirty) {
                                dispatch(setFormChangesData(true));
                            }
                        }, [validateForm, values, dirty]);
                        return (
                            <Form className="t-form" noValidate={true}>
                                <FormDebuggerComponent values={values} showDebugger={false}/>
                                <div className="t-form-controls">
                                    <div className="ts-row pdd-left-5 appointment-option-heading">
                                        <div className={'ts-col'}>
                                            <DataLabelValueComponent label={"Appointment Confirmations*"}>
                                                <div className={'data-label-value-text'}>
                                                    How would you like to receive appointment confirmations?
                                                </div>
                                            </DataLabelValueComponent>
                                        </div>
                                    </div>
                                    <div className="ts-row pdd-left-5">

                                        <div className="ts-col-12">
                                            <Field name={`communication_preferences.appointment_confirmations`}>
                                                {/*{*/}
                                                {/*    (field: FieldProps) => (*/}
                                                {/*        <FormikSelectDropdownComponent*/}
                                                {/*            options={communicationModeTypeList}*/}
                                                {/*            placeholder={"Select Option"}*/}
                                                {/*            formikField={field}*/}
                                                {/*            fullWidth={true}*/}
                                                {/*            isClearable={false}*/}
                                                {/*            required={true}*/}
                                                {/*        />*/}
                                                {/*    )*/}
                                                {/*}*/}
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            options={communicationModeTypeList}
                                                            displayWith={(option) => option.title}
                                                            valueExtractor={(option) => option.code}
                                                            required={true}
                                                            formikField={field}
                                                            id={"musculos"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <HorizontalLineComponent className={'pdd-left-5'}/>
                                    <div className="ts-row pdd-left-5 appointment-option-heading">
                                        <div className={'ts-col'}>
                                            <DataLabelValueComponent label={"Appointment Reminders*"}>
                                                <div className={'data-label-value-text'}>
                                                    How would you like to receive appointment reminders?
                                                </div>
                                            </DataLabelValueComponent>
                                        </div>
                                    </div>

                                    <div className="ts-row pdd-left-5">

                                        {/*<div className="ts-col-md-5">*/}
                                        {/*    <Field name={`communication_preferences.appointment_reminders`}>*/}
                                        {/*        {*/}
                                        {/*            (field: FieldProps) => (*/}
                                        {/*                <FormikSelectDropdownComponent*/}
                                        {/*                    options={communicationModeTypeList}*/}
                                        {/*                    placeholder={"Select Option"}*/}
                                        {/*                    formikField={field}*/}
                                        {/*                    fullWidth={true}*/}
                                        {/*                    required={true}*/}
                                        {/*                    isClearable={false}*/}
                                        {/*                />*/}
                                        {/*            )*/}
                                        {/*        }*/}
                                        {/*    </Field>*/}
                                        {/*</div>*/}
                                        <div className={'ts-col-lg-12'}>
                                            <Field name="communication_preferences.appointment_reminders">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikRadioButtonGroupComponent
                                                            options={communicationModeTypeList}
                                                            displayWith={(option) => option.title}
                                                            valueExtractor={(option) => option.code}
                                                            required={true}
                                                            formikField={field}
                                                            id={"musculos"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>

                                    </div>
                                    <HorizontalLineComponent marginBottom={true}/>
                                    <div className="ts-row pdd-left-5 appointment-option-heading">
                                        <div className="ts-col-12">
                                            <DataLabelValueComponent className={'pdd-top-25'}
                                                                     label={"How did you find us?"}>
                                                <div className={'data-label-value-text'}>
                                                    Please choose an option that best describes how you heard about us.
                                                </div>
                                            </DataLabelValueComponent>
                                        </div>
                                        <div className="ts-col-md-5">
                                            <Field name={`referral_details.source`}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            options={referralTypeList}
                                                            placeholder={"Select Option"}
                                                            formikField={field}
                                                            menuPlacement={"top"}
                                                            fullWidth={true}
                                                            isClearable={false}
                                                            onUpdate={() => {
                                                                setFieldValue('referral_details.source_info_name', '');
                                                                setFieldValue('referral_details.source_info_phone', '');
                                                                setFieldValue('referral_details.source_info_email', '');
                                                                setFieldValue('referral_details.source_info_relationship', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        values.referral_details.source === "friends_family_colleague" && <>
                                            <DataLabelValueComponent label={""}>
                                                <div className={'data-label-value-text'}>
                                                    Please complete the following:
                                                </div>
                                            </DataLabelValueComponent>
                                            <div className="ts-row">
                                                <div className="ts-col-md-6">
                                                    <Field name={`referral_details.source_info_first_name`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={"First Name"}
                                                                    placeholder={"E.g. John"}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className="ts-col-md-6">
                                                    <Field name={`referral_details.source_info_last_name`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={"Last Name"}
                                                                    placeholder={"E.g. Doe"}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className="ts-col-md-6">
                                                    <Field name={`referral_details.source_info_phone`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Phone Number'}
                                                                    type={'number'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className="ts-col-md-6">
                                                    <Field name={`referral_details.source_info_email`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    label={'Email '}
                                                                    placeholder={'example@email.com'}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row">
                                                <div className="ts-col-md-6">
                                                    <Field name={`referral_details.source_info_relationship`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    options={relationshipList}
                                                                    label={"Relationship"}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </>
                                    }
                                    {
                                        values.referral_details.source === "social_media" && <div>
                                            <div className="ts-row pdd-left-5">
                                                <div className="ts-col-md-5">
                                                    <Field name={`referral_details.source_info_name`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikSelectDropdownComponent
                                                                    label={'Please select an option:'}
                                                                    options={socialMediaPlatformList}
                                                                    placeholder={"Select Option"}
                                                                    required={true}
                                                                    menuPlacement={"top"}
                                                                    formikField={field}
                                                                    isClearable={false}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {
                                        values.referral_details.source === "comment" && <>

                                            <div className="ts-row pdd-left-5">
                                                <div className="ts-col-md-12">
                                                    <Field name={`referral_details.comment`}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikTextAreaComponent
                                                                    label={"Please explain:"}
                                                                    rows={'5'}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    required={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                        </>
                                    }
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={'next_btn'}
                                            type={"submit"}
                                            size={'large'}
                                            suffixIcon={<ImageConfig.ArrowRightCircle/>}
                                            disabled={!isValid || isSubmitting}
                                            isLoading={isSubmitting}
                                        >
                                            Next
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
            <ModalComponent isOpen={isFormSubmissionSuccessful}
                            size={'xs'}
                            closeOnBackDropClick={false}
                            className=""
                            onClose={() => setIsFormSubmissionSuccessful(false)}>
                <div className="modal__content">
                    <SuccessPopupComponent
                        title={'All Done!'}
                        description={"Congratulations, you have completed your\n" +
                            "Portal Registration."}
                        imageData={ImageConfig.Tick}
                        actions={<>
                            <div className={'success-popup-continue-and-cancel-cta'}>
                                <ButtonComponent onClick={() => navigate(CommonService._routeConfig.AppointmentList())}>
                                    Let's Proceed
                                </ButtonComponent>
                            </div>
                        </>}
                    />
                </div>
            </ModalComponent>
        </div>
    );


};

export default ClientRegistrationCommunicationReferralDetailsComponent;
